/* eslint-disable */
import Vue from "vue";
import Vuex from "vuex";
import store from "store";

// Function to detect browser language and map to supported locales
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage || "en";
  const fullLang = browserLang.toLowerCase();
  const langCode = fullLang.split("-")[0];
  // First check for exact matches (language-region)
  const exactMatches = {
    "en-us": "en-US",
    "fr-fr": "fr-FR",
    "ar-ar": "ar-AR",
    "ar-tn": "ar-AR", // Tunisia uses Arabic
    "ar-ma": "ar-AR", // Morocco uses Arabic
    "ar-dz": "ar-AR", // Algeria uses Arabic
    "sw-ke": "sw-KE",
    "sw-tz": "sw-KE", // Tanzania uses Swahili
  };

  if (exactMatches[fullLang]) {
    return exactMatches[fullLang];
  }

  // Then check by language code only
  const languageMap = {
    en: "en-US",
    fr: "fr-FR",
    ar: "ar-AR",
    sw: "sw-KE",
  };

  return languageMap[langCode] || "en-US"; // fallback to English if not supported
};

const getDefaultState = {
  image: [{ name: "", logo: "", db: "", headMaster: "" }],
  activeBuilding: "",
  activeBuildingId: "",
  base_url: process.env.VUE_APP_API_URL,
  resourcesUrl: null,
  schoolarYears: [],
  activeSchoolarYear: "",
  authProvider: "jwt", // firebase, jwt
  logo: "",
  locale: detectBrowserLanguage(), // Auto-detect browser language
  isSidebarOpen: false,
  isMobileView: false,
  isMobileMenuOpen: false,
  isMenuCollapsed: false,
  menuLayoutType: "left", // left, top, nomenu
  routerAnimation: "slide-fadein-up", // none, slide-fadein-up, slide-fadein-right, fadein, zoom-fadein
  menuColor: "white", // white, dark, gray
  theme: "default", // default, dark
  authPagesColor: "white", // white, gray, image
  primaryColor: "#4b7cf3",
  leftMenuWidth: 256,
  isMenuUnfixed: false,
  isMenuShadow: false,
  isTopbarFixed: false,
  isGrayTopbar: false,
  isContentMaxWidth: false,
  isAppMaxWidth: false,
  isGrayBackground: false,
  isCardShadow: true,
  isSquaredBorders: false,
  isBorderless: false,
  isUpgradeModalVisible: false,
  connected: {
    parents: [],
    teachers: [],
    admins: [],
  },
  power: {
    embedToken: {},
    reportsDetail: {},
  },
};

Vue.use(Vuex);

const STORED_SETTINGS = (storedSettings) => {
  const settings = {};
  Object.keys(storedSettings).forEach((key) => {
    const item = store.get(`app.settings.${key}`);
    if (key === "locale") {
      // Use browser language detection if no stored preference OR if stored preference is the old default
      const browserDetectedLang = detectBrowserLanguage();
      const shouldUseBrowserLang = !item || item === "fr-FR"; // Use browser lang if no preference or if it's the old default

      // Debug logs (uncomment if needed)
      // console.log("=== Language Detection Debug ===");
      // console.log("Stored locale:", item);
      // console.log("Browser detected:", browserDetectedLang);
      // console.log("Should use browser lang:", shouldUseBrowserLang);
      // console.log("Final locale:", shouldUseBrowserLang ? browserDetectedLang : item);

      settings[key] = shouldUseBrowserLang ? browserDetectedLang : item;
    } else {
      settings[key] = typeof item !== "undefined" ? item : storedSettings[key];
    }
  });
  return settings;
};

export default {
  namespaced: true,

  state: {
    ...STORED_SETTINGS(getDefaultState),
  },
  mutations: {
    resetState(state) {
      Object.assign(state, getDefaultState);
    },
    CHANGE_SETTING(state, payload) {
      window.localStorage.setItem(
        `app.settings.${payload.setting}`,
        payload.value
      );
      state[payload.setting] = payload.value;
    },
    SETUP_URL_SETTINGS(state, payload) {
      let queryParams = payload;
      let keys = false;
      if (payload.redirect) {
        const str = payload.redirect;
        const subs = str.substring(str.indexOf("?") + 1);
        if (str.indexOf("?") >= 0) {
          queryParams = JSON.parse(
            '{"' +
              decodeURI(subs)
                .replace(/"/g, '\\"')
                .replace(/&/g, '","')
                .replace(/=/g, '":"') +
              '"}'
          );
        }
      }
      delete queryParams.redirect;
      keys = Object.keys(queryParams);
      if (keys.length) {
        keys.forEach((key) => {
          let value;
          switch (queryParams[key]) {
            case "false":
              value = false;
              break;
            case "true":
              value = true;
              break;
            default:
              value = queryParams[key];
              break;
          }
          if (key in state) {
            state[key] = value;
          }
        });
      }
    },
    SET_PRIMARY_COLOR(state, payload) {
      const { color } = payload;
      const addStyles = () => {
        const styleElement = document.querySelector("#primaryColor");
        if (styleElement) {
          styleElement.remove();
        }
        const body = document.querySelector("body");
        const styleEl = document.createElement("style");
        const css = document.createTextNode(
          `:root { --kit-color-primary: ${color};}`
        );
        styleEl.setAttribute("id", "primaryColor");
        styleEl.appendChild(css);
        body.appendChild(styleEl);
      };
      addStyles();
      state.primaryColor = color;
      store.set("app.settings.primaryColor", color);
    },
    SET_THEME(state, payload) {
      const { theme } = payload;
      const nextTheme = theme === "dark" ? "dark" : "default";
      document.querySelector("html").setAttribute("data-kit-theme", nextTheme);
      state.theme = nextTheme;
      store.set("app.settings.theme", nextTheme);
    },
    showUpgradeModal(state) {
      console.log("showUpgradeModal");
      state.isUpgradeModalVisible = true;
    },
    hideUpgradeModal(state) {
      state.isUpgradeModalVisible = false;
    },
    SET_RESOURCES_URL(state, payload) {
      state.resourcesUrl = payload;
    },
    SET_IMAGE(state, payload) {
      state.image = payload;
    },
    CHANGE_CONNECTED(state, payload) {
      window.localStorage.setItem(
        `app.settings.connected.${payload.list}`,
        payload.value
      );
      state.connected[payload.list] = payload.value;
    },
    CHANGE_POWER_CONFIG(state, payload) {
      window.localStorage.setItem(
        `app.settings.power.${payload.list}`,
        payload.value
      );
      state.power[payload.list] = payload.value;
    },
  },
  actions: {
    //this action check if the client's pack has the access of btns
    packHasBtnAccess({ state, rootState, commit }, btnkey) {
      if (!btnkey) {
        commit("showUpgradeModal");
        return false;
      }
      const schoolDetails = rootState.user.building.find(
        (el) => el.dbName == state.activeBuilding
      );
      if (
        schoolDetails.pack.access.edManager.allowedExportBtns.includes("all") ||
        schoolDetails.pack.access.edManager.allowedExportBtns.includes(btnkey)
      )
        return true;
      else {
        commit("showUpgradeModal");
        return false;
      }
    },
    /* packBtnDisabled: function name({ state, rootState, commit }, btnKey) {
      const schoolDetails = rootState.user.building.find(
        (el) => el.dbName == state.activeBuilding
      );
      return !(
        schoolDetails.pack.access.edManager.allowedExportBtns.includes("all") ||
        schoolDetails.pack.access.edManager.allowedExportBtns.includes(btnKey)
      );
    },*/
    SET_RESOURCES_URL({ commit }, url) {
      commit("SET_RESOURCES_URL", url);
    },
    SET_IMAGE({ commit }, image) {
      commit("SET_IMAGE", image);
    },
    CHANGE_SETTING({ commit }, obj) {
      commit("CHANGE_SETTING", obj);
    },
    CHANGE_CONNECTED() {
      commit("CHANGE_CONNECTED", obj);
    },
    CHANGE_POWER_CONFIG() {
      commit("CHANGE_POWER_CONFIG", obj);
    },
    RESET_SETTINGS({ commit }) {
      commit("resetState");
    },
    openUpgradeModal({ commit }) {
      commit("showUpgradeModal");
    },
    closeUpgradeModal({ commit }) {
      commit("hideUpgradeModal");
    },
  },
  getters: {
    activeSchoolarYearGetter(state) {
      return state.activeSchoolarYear;
    },
    btnAccessGetter: (state, getters, rootState) => {
      const building = rootState.user.building?.find(
        (el) => el.dbName == state.activeBuilding
      );
      return building?.pack?.access?.edManager?.allowedExportBtns || [];
    },
    packDetailsGetter: (state, getters, rootState) => {
      const building = rootState.user.building?.find(
        (el) => el.dbName == state.activeBuilding
      );
      return building?.pack || {};
    },
    state: (state) => state,
    connected: (state) => state.connected,
    power: (state) => state.power,
  },
};
